import * as React from 'react';
import Select, { ValueType } from '@atlaskit/select';
import { Field } from '@atlaskit/form';
import AddIcon from '@atlaskit/icon/glyph/editor/add';
import DeleteIcon from '@atlaskit/icon/glyph/editor/remove';
import Button, { ButtonGroup } from '@atlaskit/button';
import Textfield from '@atlaskit/textfield';
import { AddOn } from '@css-xp/graphtypes';
import { PageLayer } from '@atlassian/cssxp-typescommon';
import { ValidationState } from '@atlaskit/select/dist/types/types';

interface Props {
  availableAddOns: AddOnOption[];
  selectedAddOns: AddOnOption[];
  removeAddOn: (index: number) => void;
  addAdditionalAddOn: () => void;
  layoutOptions?: {
    insertEmptyFirstColumn?: boolean;
    showLabels?: boolean;
  };
}

export interface AddOnSelectItem {
  label: AddOnOption['name'];
  value: {
    offeringId: AddOnOption['offeringId'];
  };
}

export type AddOnOption = Pick<AddOn, 'name' | 'offeringId' | 'units'>;

export enum SelectValidationState {
  DEFAULT = 'default',
  ERROR = 'error',
  SUCCESS = 'success',
}

const validate = (value: any) => (!value ? 'EMPTY' : undefined);

const getValidationState = (error: any, valid: boolean): ValidationState => {
  if (!error && !valid) {
    return SelectValidationState.DEFAULT;
  }
  if (valid === true) {
    return SelectValidationState.SUCCESS;
  }
  return SelectValidationState.ERROR;
};

const StatuspageAvailableAddOnsFormField = ({
  availableAddOns,
  selectedAddOns,
  removeAddOn,
  addAdditionalAddOn,
  layoutOptions,
}: Props) => {
  const availableAddOnOptions: AddOnSelectItem[] =
    availableAddOns?.map((addOn: AddOn) => ({
      label: addOn.name,
      value: {
        offeringId: addOn.offeringId,
      },
    })) ?? [];

  return (
    <>
      {selectedAddOns.map((addOn, index, arr) => {
        return (
          <tr key={`selected-add-on-${index}`} data-testid={`selected-add-on-${index}`}>
            {layoutOptions?.insertEmptyFirstColumn && <td />}
            <td>
              <Field<ValueType<AddOnSelectItem>, HTMLSelectElement>
                name={`selectedAddOns[${index}].selectedAddOn`}
                {...(addOn.offeringId
                  ? {
                      defaultValue: {
                        label: addOn.name ?? '',
                        value: {
                          offeringId: addOn.offeringId,
                        },
                      },
                    }
                  : undefined)}
                label={layoutOptions?.showLabels && index === 0 ? 'Add-on' : undefined}
                isRequired
                validate={validate}
              >
                {({ fieldProps, error, meta: { valid } }) => (
                  <Select
                    {...fieldProps}
                    options={availableAddOnOptions}
                    placeholder={'Select an add-on'}
                    styles={{
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: PageLayer.SIDEBAR_SELECT,
                      }),
                    }}
                    testId={`add-on-select-${index}`}
                    menuPortalTarget={document.body}
                    validationState={getValidationState(error, valid)}
                  />
                )}
              </Field>
            </td>
            <td>
              <Field
                name={`selectedAddOns[${index}].quantity`}
                defaultValue={`${addOn.units ?? 1}`}
                label={layoutOptions?.showLabels && index === 0 ? 'Quantity' : undefined}
              >
                {({ fieldProps }) => <Textfield {...fieldProps} type="number" min="1" max="10" />}
              </Field>
            </td>
            <td style={{ verticalAlign: 'bottom' }}>
              <span>
                <ButtonGroup>
                  <Button
                    testId={`remove-add-on-${index}`}
                    onClick={() => removeAddOn(index)}
                    style={{
                      height: '40px',
                    }}
                  >
                    <DeleteIcon label="remove" />
                  </Button>
                  {arr.length - 1 === index && (
                    <Button
                      testId={`add-additional-add-on-${index}`}
                      onClick={addAdditionalAddOn}
                      style={{
                        height: '40px',
                      }}
                    >
                      <AddIcon label="add more add-ons" />
                    </Button>
                  )}
                </ButtonGroup>
              </span>
            </td>
          </tr>
        );
      })}
    </>
  );
};

export default StatuspageAvailableAddOnsFormField;
